import {
  EventStatus,
  EventRegistrationStatus,
  EventOptionTypes,
  SponsorshipTypeMapping,
  CronJobStatus,
} from './commonUtility';

const EVENT_STATUS_LABELS = {
  [EventStatus.PRIVATE]: 'Private',
  [EventStatus.TENTATIVE]: 'Tentative',
  [EventStatus.ACTIVE]: 'Active',
  [EventStatus.DRAFT]: 'Draft',
  [EventStatus.REG_CLOSED]: 'Registration Closed',
};

const REGISTRATION_STATUS_LABELS = {
  [EventRegistrationStatus.TO_BE_DELETED]: 'To be Deleted',
  [EventRegistrationStatus.PAYMENT_REFUNDED]: 'Payment Refunded',
  [EventRegistrationStatus.TO_BE_PAID]: 'To be Paid',
  [EventRegistrationStatus.REFUND_PENDING]: 'Refund Pending',
  [EventRegistrationStatus.PAID]: 'Paid',
  [EventRegistrationStatus.PAYMENT_CONFIRMED]: 'Payment Confirmed',
  [EventRegistrationStatus.NO_CHARGE_EVENT]: 'No Charge Event',
  [EventRegistrationStatus.FEE_WAIVER]: 'Fee Waiver',
  [EventRegistrationStatus.PAYMENT_PROCESSING]: 'Payment Processing',
};

const EVENT_OPTION_TYPE_LABELS = {
  [EventOptionTypes.FOOD]: 'Food',
  [EventOptionTypes.REGISTRATION]: 'Registration',
  [EventOptionTypes.SPONSORSHIP]: 'Sponsorship',
};

const CRON_JOB_STATUS_LABELS = {
  [CronJobStatus.RUN_SUCCESSFULLY]: 'Run successfully',
  [CronJobStatus.FAILED]: 'Failed to run schedule',
  [CronJobStatus.NOT_STARTED_YET]: 'Schedule not initiated yet',
};

const CHAPTER_ARRAY_FIELDS = [
  'online_for_chapters',
  'webcast_available_for_chapters',
  'offline_for_chapters',
  'local_chapter_ids',
  'mail_preference_chapter_ids',
];

const USER_ID_FIELDS = ['create_by_id', 'edit_by_id', 'approved_by_id'];

const SPONSORSHIP_PERK_METADATA_FIELDS = [
  'id',
  'create_by_id',
  'edit_by_id',
  'created_at',
  'updated_at',
  'event_option_id',
];

const stripSponsorshipPerkMetadata = (perk) => {
  if (!perk || typeof perk !== 'object' || Array.isArray(perk)) return perk;
  const out = {...perk};
  SPONSORSHIP_PERK_METADATA_FIELDS.forEach((key) => {
    delete out[key];
  });
  return out;
};

const toNumeric = (value) =>
  typeof value === 'string' ? Number(value.trim()) : value;

const resolveIdFromMap = (idMap, id, fallbackPrefix) => {
  if (id == null || id === '') return id;
  if (!idMap) return id;
  const numId = toNumeric(id);
  if (Number.isNaN(numId)) return id;
  return idMap.get(numId) || `${fallbackPrefix} ${numId}`;
};

const mapEventStatus = (status) => {
  const numeric = toNumeric(status);
  return EVENT_STATUS_LABELS[numeric] ?? status;
};

const mapEventRegistrationStatus = (status) => {
  const numeric = toNumeric(status);
  return REGISTRATION_STATUS_LABELS[numeric] ?? status;
};

const mapSponsorshipType = (value) => {
  if (value == null || value === '') return value;
  const numeric = toNumeric(value);
  return SponsorshipTypeMapping[numeric] ?? value;
};

const mapEventOptionType = (value) => {
  if (value == null || value === '') return value;
  const key = String(value).toLowerCase();
  return EVENT_OPTION_TYPE_LABELS[key] ?? value;
};

const resolveChapterIdArray = (arr, chapterIdToName) => {
  if (!Array.isArray(arr) || arr.length === 0) return arr;
  const allNumeric = arr.every(
    (id) =>
      typeof id === 'number' ||
      (typeof id === 'string' && /^\d+$/.test(String(id).trim())),
  );
  if (!allNumeric || !chapterIdToName) return arr;
  const names = arr.map((id) => {
    const numId = typeof id === 'number' ? id : parseInt(id, 10);
    return chapterIdToName.get(numId) || `Chapter ${numId}`;
  });
  return [...names].sort((a, b) => String(a).localeCompare(String(b)));
};

const formatNamedOptions = (options) => {
  if (!Array.isArray(options) || options.length === 0) return options;
  return options
    .map((option) => {
      if (option == null) return 'Unknown';
      if (typeof option === 'string') return option;
      if (typeof option === 'object' && option.name) return option.name;
      return 'Unknown';
    })
    .sort((a, b) => String(a).localeCompare(String(b)));
};

const mapCronJobStatus = (status) => {
  const numeric = toNumeric(status);
  return CRON_JOB_STATUS_LABELS[numeric] ?? status;
};

const formatScheduleDate = (value) => {
  if (!value) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
};

const mapBooleanLabel = (value) => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return value;
};

/**
 * Converts raw audit-log JSON into human-readable values (names/labels instead of IDs/enums).
 */
const resolveUserIdFields = (obj, userIdToDisplayName) => {
  if (!obj || typeof obj !== 'object' || !userIdToDisplayName) return obj;
  const out = {...obj};
  USER_ID_FIELDS.forEach((key) => {
    if (key in out && out[key] != null) {
      out[key] = resolveIdFromMap(userIdToDisplayName, out[key], 'User');
    }
  });
  return out;
};

export const transformAuditLogValue = (
  value,
  entityType,
  {chapterIdToName, eventTypeIdToName, eventIdToName, userIdToDisplayName} = {},
) => {
  if (!value || typeof value !== 'object') return value;

  const out = resolveUserIdFields(value, userIdToDisplayName);

  if (entityType === 'EVENT' && 'status' in out) {
    out.status = mapEventStatus(out.status);
  }

  if (entityType === 'EVENT_REGISTRATION' && 'status' in out) {
    out.status = mapEventRegistrationStatus(out.status);
  }

  if (entityType === 'EVENT_REFUND') {
    if ('registration_status' in out) {
      out.registration_status = mapEventRegistrationStatus(out.registration_status);
    }
    if ('refund_percentage' in out && out.refund_percentage != null && out.refund_percentage !== '') {
      const numeric = toNumeric(out.refund_percentage);
      if (!Number.isNaN(numeric)) {
        out.refund_percentage = `${numeric}%`;
      }
    }
    if ('refund_to_account' in out) {
      out.refund_to_account = mapBooleanLabel(out.refund_to_account);
    }
  }

  if (entityType === 'EVENT_EMAIL_TEMPLATE' && 'cron_job_status' in out) {
    out.cron_job_status = mapCronJobStatus(out.cron_job_status);
  }

  if (entityType === 'EVENT_EMAIL_TEMPLATE' && 'schedule_date' in out) {
    out.schedule_date = formatScheduleDate(out.schedule_date);
  }

  if (entityType === 'COUPON_CODE') {
    ['percentage_discount', 'apply_to_whole_cart', 'apply_to_food'].forEach((key) => {
      if (key in out) {
        out[key] = mapBooleanLabel(out[key]);
      }
    });
    if ('start_date' in out) {
      out.start_date = formatScheduleDate(out.start_date);
    }
    if ('end_date' in out) {
      out.end_date = formatScheduleDate(out.end_date);
    }
    // Prefer event name over raw event_id in the compare UI
    if ('event_id' in out || 'event_name' in out) {
      const resolvedName =
        out.event_name ||
        resolveIdFromMap(eventIdToName, out.event_id, 'Event');
      out.event_name =
        resolvedName == null || resolvedName === '' ? 'N/A' : resolvedName;
      delete out.event_id;
    }
  }

  if (entityType === 'EVENT_OPTION') {
    ['start_date', 'end_date', 'created_at'].forEach((key) => {
      if (key in out) {
        out[key] = formatScheduleDate(out[key]);
      }
    });
  }

  if ('local_chapter_id' in out) {
    out.local_chapter_id = resolveIdFromMap(
      chapterIdToName,
      out.local_chapter_id,
      'Chapter',
    );
  }

  if ('event_type_id' in out) {
    out.event_type_id = resolveIdFromMap(
      eventTypeIdToName,
      out.event_type_id,
      'Event Type',
    );
  }

  if ('sponsorship_type' in out && out.sponsorship_type != null) {
    out.sponsorship_type = mapSponsorshipType(out.sponsorship_type);
  }

  if ('event_option_type' in out && out.event_option_type != null) {
    out.event_option_type = mapEventOptionType(out.event_option_type);
  }

  CHAPTER_ARRAY_FIELDS.forEach((key) => {
    if (key in out) {
      out[key] = resolveChapterIdArray(out[key], chapterIdToName);
    }
  });

  if (entityType === 'EVENT_REGISTRATION') {
    if ('food_options' in out) {
      out.food_options = formatNamedOptions(out.food_options);
    }
    if ('registration_options' in out) {
      out.registration_options = formatNamedOptions(out.registration_options);
    }
  }

  if (
    out.sponsorship_perk &&
    typeof out.sponsorship_perk === 'object' &&
    !Array.isArray(out.sponsorship_perk)
  ) {
    out.sponsorship_perk = stripSponsorshipPerkMetadata(out.sponsorship_perk);
  }

  return out;
};
